// HR recording leave somebody already took.
//
// Requested as "a provision so that HR can mark leaves which were taken by the
// employee without notifying the manager" — an absence that happened, was never
// filed, and has to end up on the record after the fact.
//
// The SERVER already allowed this; there was no way to reach it. enforce_leave_request
// refuses a backdated start date only `if not is_mgr`, and its own comment says so:
// "no backdating for staff. HR keeps it, to record leave taken offline or fix a
// record" (20260731100000:50-52). The same function refuses an INSERT arriving as
// anything but 'pending' only for non-HR (:62-66). So people:full has always been
// able to file an approved, backdated row — but ApplyLeaveForm is mounted once, on
// My Space, for yourself. There was no surface for doing it to somebody else.
//
// ── Who hears about it ───────────────────────────────────────────────────────
// Nobody is asked to approve anything. The HR fan-out fires ONLY on a fresh
// PENDING insert (20260731100000:130+), so filing this as approved raises no
// request and pages no approver. The EMPLOYEE is notified — that branch fires on
// `tg_op = 'INSERT' and new.status <> 'pending'` — which is deliberate and worth
// saying out loud in the UI: somebody's leave balance is being spent, and finding
// out silently is how a balance becomes unreconcilable.
//
// ── What the database will still refuse ──────────────────────────────────────
//   * A PAID type over its entitlement — "insufficient X leave balance". An
//     unnotified absence very often has to be unpaid for exactly this reason, and
//     that needs an unpaid leave type to exist in Org → Leave types.
//   * A range covering no working day at all.
//   * A half-day spanning more than one day.
//   * A locked payroll period (assert_period_open) — leave_requests is gated, so
//     recording into a closed month needs an amendment window.
// All four surface as the database's own message rather than being re-worded.
//
// On the Dialog and Field primitives (HRP-10, HRP-11): it was one of two modals on
// the record built two different ways, with a private field recipe, a native
// select, and one error line above the form whichever field it was about.
import { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Select } from './primitives';
import { Dialog } from './dialog';
import { DateField, Field, TextArea } from './field';
import { InlineStatus } from './feedback';
import { supabase } from '../lib/supabase';
import { fmtDayRange, isoDay, orgToday } from '../lib/dates';
import { useOrgDates } from '../lib/orgSettings';
import { workingDays } from '../lib/workdays';

type LeaveTypeLite = {
  id: string;
  label: string;
  color: string;
  paid: boolean;
  archived?: boolean;
};

/** Which field a problem belongs to, so it is said beside that field. */
type Errors = { type?: string; start?: string; end?: string; reason?: string };

export function RecordLeaveModal({
  profileId,
  personName,
  types,
  uid,
  holidays,
  onClose,
  onFiled,
}: {
  profileId: string;
  personName: string | null;
  types: LeaveTypeLite[];
  /** The recorder. Stamped as `approver`, exactly as the approve path does — the
   *  row must say who put it there. */
  uid: string;
  /** Org holidays keyed by date (only the dates are read), so the charged-day count matches the server's
   *  leave_working_days rather than counting raw calendar days. */
  holidays?: Readonly<Record<string, unknown>>;
  onClose: () => void;
  /** After the row lands, with what was charged, so the page can say so. */
  onFiled: (charged: number, typeLabel: string) => void;
}) {
  const { workdays, tz } = useOrgDates();
  // A retired type is not offered: the database refuses new leave on it
  // ("… leave is no longer offered", 20260930170000).
  const offered = types.filter((t) => !t.archived);
  const [typeId, setTypeId] = useState(offered[0]?.id ?? '');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [halfDay, setHalfDay] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  // The database's refusal: about the request as a whole, not one field.
  const [refusal, setRefusal] = useState<string | null>(null);

  const holidaySet = useMemo(() => new Set(Object.keys(holidays ?? {})), [holidays]);
  const singleDay = !!start && (!end || end === start);
  useEffect(() => {
    if (!singleDay && halfDay) setHalfDay(false);
  }, [singleDay, halfDay]);

  // What the server will charge, by the same rule it uses: working days in the
  // range, less half if this is a half day. Weekends and holidays inside a span
  // are not leave, so a raw date difference would over-state it.
  const days = start ? workingDays(start, end || start, workdays, holidaySet) : 0;
  const charged = halfDay && singleDay ? 0.5 : days;
  const type = offered.find((t) => t.id === typeId) ?? null;
  // The span in words before it is filed: the date boxes read in the
  // browser's order, the org's may differ (UI-CONVENTIONS §2, critique
  // 2026-10-05 #2). Empty while the range is one the form would refuse.
  const range = fmtDayRange(start, end || start, isoDay(orgToday(tz)));

  const submit = async () => {
    if (busy) return;
    const found: Errors = {};
    if (!typeId) found.type = 'Pick a leave type.';
    if (!start) found.start = 'Pick the day the leave started.';
    else if (end && end < start) found.end = 'The end date cannot be before the start date.';
    else if (days < 1)
      found.start =
        'That range covers no working days — the database refuses leave over only weekends or holidays.';
    if (!reason.trim())
      found.reason = 'Say what this records. It is the only account of why the balance moved.';
    setErrors(found);
    if (Object.keys(found).length || !supabase) return;

    setBusy(true);
    setRefusal(null);
    // Filed already decided, the same shape the approve path writes. day_count is
    // server-computed by the trigger, so it is deliberately not sent.
    const { error } = await supabase.from('leave_requests').insert({
      profile_id: profileId,
      leave_type_id: typeId,
      start_date: start,
      end_date: end || start,
      half_day: halfDay && singleDay,
      reason: reason.trim(),
      status: 'approved',
      approver: uid,
      decided_at: new Date().toISOString(),
    });
    if (error) {
      setRefusal(error.message);
      setBusy(false);
      return;
    }
    onFiled(charged, type?.label ?? 'leave');
    onClose();
  };

  return (
    <Dialog
      open
      title="Record leave taken"
      size="sm"
      onClose={onClose}
      dirty={!!(start || end || reason.trim())}
      footer={(requestClose) => (
        <>
          <Button variant="ghost" onClick={requestClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} onClick={() => void submit()}>
            Record leave
          </Button>
        </>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-4)' }}>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--hf-text-sm)',
            lineHeight: 'var(--hf-leading-body)',
            color: 'var(--hf-ink-3)',
          }}
        >
          For {personName ?? 'this person'}. This goes on the record as already approved — no
          request is raised and no approver is asked. {personName ?? 'They'} will be notified,
          because it spends their balance.
        </p>

        {refusal && <InlineStatus tone="error">{refusal}</InlineStatus>}

        <Field label="Leave type" error={errors.type}>
          {(control) => (
            <Select
              {...control}
              value={typeId}
              onChange={setTypeId}
              options={offered.map((t) => ({
                value: t.id,
                label: `${t.label}${t.paid ? '' : ' (unpaid)'}`,
              }))}
            />
          )}
        </Field>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--hf-space-3)' }}>
          <DateField
            label="From"
            required
            error={errors.start}
            style={{ flex: '1 1 10rem' }}
            value={start}
            onChange={setStart}
          />
          <DateField
            label="To · optional"
            error={errors.end}
            style={{ flex: '1 1 10rem' }}
            value={end}
            onChange={setEnd}
          />
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 'var(--hf-space-2)',
          }}
        >
          <Checkbox
            label="Half day (0.5)"
            checked={halfDay}
            disabled={!singleDay}
            onChange={setHalfDay}
          />
          {/* Disabled with the reason beside it, never a silent no-op. */}
          {!singleDay && (
            <span style={{ fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-3)' }}>
              {start ? 'One day only; clear “To”.' : 'Pick a date first.'}
            </span>
          )}
        </div>

        <Field label="What this records" required error={errors.reason}>
          <TextArea
            rows={2}
            value={reason}
            onChange={setReason}
            placeholder="Absent, told nobody at the time — confirmed with them on the 14th"
          />
        </Field>

        {start && (
          <div style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-2)' }}>
            {range ? `${range} · charges ` : 'Charges '}
            <strong>{charged}</strong> day{charged === 1 ? '' : 's'} of {type?.label ?? 'leave'}
            {type && !type.paid && (
              <span style={{ color: 'var(--hf-amber-text)' }}>
                {' '}
                · unpaid, so it can reduce their pay
              </span>
            )}
            {days === 0 && (
              <span style={{ color: 'var(--hf-amber-text)' }}>
                {' '}
                · no working days in that range
              </span>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
}
